'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'
import { apiBaseUrl, saveAppAuthSession } from '@/lib/app-auth-session'
import { DEFAULT_UI } from '@/lib/ui-defaults'
import type { UIStrings } from '@/lib/schemas-server'

type Tab = 'magic_link' | 'password'
type PasswordMode = 'signin' | 'signup'
type AuthUi = NonNullable<UIStrings['auth']>

/** Map auth API errors to user-facing copy, preferring i18n strings when available. */
function authErrorCopy(message: string, context: Tab, ui?: AuthUi): string {
  const fb = DEFAULT_UI.auth
  const lower = message.toLowerCase()
  if (context === 'password' && (lower.includes('invalid') || lower.includes('credential') || lower.includes('password'))) {
    return ui?.error_wrong_password ?? fb?.error_wrong_password ?? ''
  }
  if (context === 'magic_link' && (lower.includes('expired') || lower.includes('invalid') || lower.includes('link'))) {
    return ui?.error_link_expired ?? fb?.error_link_expired ?? ''
  }
  if (lower.includes('not confirmed') || lower.includes('confirm your email')) {
    return ui?.error_not_confirmed ?? fb?.error_not_confirmed ?? ''
  }
  if (lower.includes('already registered') || lower.includes('already exists') || lower.includes('user already')) {
    return ui?.error_already_registered ?? fb?.error_already_registered ?? ''
  }
  if (lower.includes('rate limit') || lower.includes('too many') || lower.includes('security purposes')) {
    return ui?.error_rate_limit ?? fb?.error_rate_limit ?? ''
  }
  if (lower.includes('signup') && lower.includes('disabled')) {
    return String(ui?.error_signup_disabled ?? fb?.error_signup_disabled ?? '')
  }
  return ui?.error_generic ?? fb?.error_generic ?? ''
}

interface VhfAuthModalProps {
  isOpen: boolean
  onClose: () => void
  /** Redirect URL after magic link login. Defaults to /vhf-course. */
  redirectTo?: string
  /** Locale for magic link callback URL (e.g. "en"). */
  locale?: string
  /**
   * When true, renders the form inline (no backdrop, no fixed overlay).
   * The parent page is responsible for centering.
   */
  inline?: boolean
  /** Pre-seed the active tab. Defaults to 'magic_link'. */
  initialTab?: Tab
  /** Pre-seed password form mode. Defaults to 'signin'. */
  initialMode?: PasswordMode
  /** i18n strings for all UI labels. Falls back to English if not provided. */
  ui?: AuthUi
}

export default function VhfAuthModal({
  isOpen,
  onClose,
  redirectTo: _redirectTo,
  locale = 'en',
  inline = false,
  initialTab = 'magic_link',
  initialMode = 'signin',
  ui,
}: VhfAuthModalProps) {
  const [tab, setTab] = useState<Tab>(initialTab)
  const [mode, setMode] = useState<PasswordMode>(initialMode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  // Re-seed tab/mode if props change (e.g. navigating from checkout intent)
  useEffect(() => { setTab(initialTab) }, [initialTab])
  useEffect(() => { setMode(initialMode) }, [initialMode])

  // Focus trap — only active in modal mode (not inline)
  useEffect(() => {
    if (!isOpen || inline) return
    const dialog = dialogRef.current
    if (!dialog) return

    const focusable = dialog.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    focusable[0]?.focus()

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return }
      if (e.key !== 'Tab') return
      const els = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      )
      if (els.length === 0) return
      const first = els[0]
      const last = els[els.length - 1]
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus() }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus() }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, inline, onClose])

  const emailSchema = useMemo(() => {
    const fb = DEFAULT_UI.auth
    return z.string().email(
      ui?.validation_email_invalid ?? fb?.validation_email_invalid ?? '',
    )
  }, [ui])
  const passwordSchema = useMemo(() => {
    const fb = DEFAULT_UI.auth
    return z.string().min(
      8,
      ui?.validation_password_min ?? fb?.validation_password_min ?? '',
    )
  }, [ui])

  if (!isOpen) return null

  const fbAuth = DEFAULT_UI.auth
  const apiUrl = apiBaseUrl()

  const reset = () => {
    setError(null)
    setSuccess(null)
    setLoading(false)
  }

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault()
    reset()
    const parsed = emailSchema.safeParse(email)
    if (!parsed.success) { setError(parsed.error.issues[0].message); return }

    setLoading(true)
    const res = await fetch(`${apiUrl}/api/auth/v1/magic-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    setLoading(false)
    if (!res.ok) {
      setError(ui?.error_generic ?? fbAuth?.error_generic ?? '')
      return
    }
    setSuccess(ui?.success_magic_link ?? fbAuth?.success_magic_link ?? '')
  }

  const handlePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    reset()

    const emailParsed = emailSchema.safeParse(email)
    if (!emailParsed.success) { setError(emailParsed.error.issues[0].message); return }
    const pwParsed = passwordSchema.safeParse(password)
    if (!pwParsed.success) { setError(pwParsed.error.issues[0].message); return }

    setLoading(true)

    if (mode === 'signup') {
      const res = await fetch(`${apiUrl}/api/auth/v1/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          full_name: fullName.trim() || null,
          locale,
        }),
      })
      setLoading(false)
      if (!res.ok) {
        let msg = res.statusText
        try {
          const j = (await res.json()) as { detail?: string | { msg?: string } }
          if (typeof j.detail === 'string') msg = j.detail
        } catch { /* ignore */ }
        setError(authErrorCopy(msg, 'password', ui))
        return
      }
      const data = (await res.json()) as {
        access_token: string
        refresh_token: string
        expires_in: number
      }
      saveAppAuthSession(data)
      window.dispatchEvent(new CustomEvent('fh-app-auth-changed'))
      onClose()
      return
    }

    const res = await fetch(`${apiUrl}/api/auth/v1/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    setLoading(false)
    if (!res.ok) {
      let msg = res.statusText
      try {
        const j = (await res.json()) as { detail?: string }
        if (typeof j.detail === 'string') msg = j.detail
      } catch { /* ignore */ }
      setError(authErrorCopy(msg, 'password', ui))
      return
    }
    const data = (await res.json()) as {
      access_token: string
      refresh_token: string
      expires_in: number
    }
    saveAppAuthSession(data)
    window.dispatchEvent(new CustomEvent('fh-app-auth-changed'))
    onClose()
  }

  const card = (
    <div
      ref={dialogRef}
      className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
        <h2 id="auth-modal-title" className="text-lg font-semibold text-heading">
          {ui?.heading ?? fbAuth?.heading ?? ''}
        </h2>
        {!inline && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label={String(ui?.aria_close_auth ?? fbAuth?.aria_close_auth ?? '')}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-neutral-100">
        {(['magic_link', 'password'] as const).map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); reset() }}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              tab === t
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted hover:text-content'
            }`}
          >
            {t === 'magic_link'
              ? (ui?.tab_magic_link ?? fbAuth?.tab_magic_link ?? '')
              : (ui?.tab_password ?? fbAuth?.tab_password ?? '')}
          </button>
        ))}
      </div>

      <div className="px-6 py-5">
        {/* Status messages */}
        {error && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
            {success}
          </div>
        )}

        {tab === 'magic_link' ? (
          <form onSubmit={handleMagicLink} className="space-y-4">
            <div>
              <label htmlFor="ml-email" className="block text-sm font-medium text-content mb-1">
                {ui?.label_email ?? fbAuth?.label_email ?? ''}
              </label>
              <input
                id="ml-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder={ui?.placeholder_email ?? fbAuth?.placeholder_email ?? ''}
                className="w-full rounded-interactive border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-interactive bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60 transition-colors focus-visible:outline-2 focus-visible:outline-interactive-focus focus-visible:outline-offset-2"
            >
              {loading
                ? (ui?.btn_sending ?? fbAuth?.btn_sending ?? '')
                : (ui?.btn_send_magic_link ?? fbAuth?.btn_send_magic_link ?? '')}
            </button>
            <p className="text-xs text-muted text-center">
              {ui?.magic_link_hint ?? fbAuth?.magic_link_hint ?? ''}
            </p>
          </form>
        ) : (
          <form onSubmit={handlePassword} className="space-y-4">
            {/* Mode toggle */}
            <div className="flex gap-4 text-sm">
              {(['signin', 'signup'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => { setMode(m); reset() }}
                  className={`font-medium transition-colors ${
                    mode === m ? 'text-primary' : 'text-muted hover:text-content'
                  }`}
                >
                  {m === 'signin'
                    ? (ui?.btn_sign_in ?? fbAuth?.btn_sign_in ?? '')
                    : (ui?.btn_create_account ?? fbAuth?.btn_create_account ?? '')}
                </button>
              ))}
            </div>

            {mode === 'signup' && (
              <div>
                <label htmlFor="pw-name" className="block text-sm font-medium text-content mb-1">
                  {ui?.label_full_name ?? fbAuth?.label_full_name ?? ''}
                </label>
                <input
                  id="pw-name"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={ui?.placeholder_full_name ?? fbAuth?.placeholder_full_name ?? ''}
                  className="w-full rounded-interactive border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                />
              </div>
            )}

            <div>
              <label htmlFor="pw-email" className="block text-sm font-medium text-gray-700 mb-1">
                {ui?.label_email ?? fbAuth?.label_email ?? ''}
              </label>
              <input
                id="pw-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder={ui?.placeholder_email ?? fbAuth?.placeholder_email ?? ''}
                className="w-full rounded-interactive border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              />
            </div>

            <div>
              <label htmlFor="pw-password" className="block text-sm font-medium text-content mb-1">
                {ui?.label_password ?? fbAuth?.label_password ?? ''}
              </label>
              <input
                id="pw-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="--------"
                className="w-full rounded-interactive border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-interactive bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60 transition-colors focus-visible:outline-2 focus-visible:outline-interactive-focus focus-visible:outline-offset-2"
            >
              {loading
                ? (mode === 'signin'
                    ? (ui?.btn_signing_in ?? fbAuth?.btn_signing_in ?? '')
                    : (ui?.btn_creating ?? fbAuth?.btn_creating ?? ''))
                : (mode === 'signin'
                    ? (ui?.btn_sign_in ?? fbAuth?.btn_sign_in ?? '')
                    : (ui?.btn_create_account ?? fbAuth?.btn_create_account ?? ''))}
            </button>
          </form>
        )}
      </div>
    </div>
  )

  if (inline) {
    return card
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
    >
      {card}
    </div>
  )
}
